import { useTranslation } from 'react-i18next';
import { useLocationStore } from '@/features/location/store/location-store';

/**
 * Component to prompt user for location permission
 * Shows when permission is denied or disabled
 */
export function LocationPermissionPrompt() {
  const { t } = useTranslation();
  const locationPermission = useLocationStore((s) => s.locationPermission);
  const getCurrentLocation = useLocationStore((s) => s.getCurrentLocation);

  if (locationPermission !== 'denied' && locationPermission !== 'disabled') {
    return null;
  }

  const isDenied = locationPermission === 'denied';
  const isDisabled = locationPermission === 'disabled';

  return (
    <div className="absolute top-20 left-1/2 transform -translate-x-1/2 z-20 max-w-md">
      <div className="bg-white rounded-lg shadow-monta-lg p-4 border border-monta-gray-200 mx-4">
        <div className="flex items-start gap-3">
          {/* Icon */}
          <div className="w-10 h-10 bg-monta-orange/10 rounded-full flex items-center justify-center flex-shrink-0">
            <svg
              className="w-5 h-5 text-monta-orange"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>

          {/* Content */}
          <div className="flex-1">
            <h3 className="font-heading font-semibold text-sm text-monta-gray-900 mb-1">
              {isDenied
                ? t('search.locationDeniedTitle')
                : t('search.locationDisabledTitle')}
            </h3>
            <p className="text-xs text-monta-gray-700 mb-3">
              {isDenied
                ? t('search.locationDeniedMessage')
                : t('search.locationDisabledMessage')}
            </p>

            {/* Actions */}
            <div className="flex gap-2">
              {isDenied && (
                <button
                  onClick={() => {
                    // Open browser settings (this is browser-dependent)
                    window.open(
                      'https://support.google.com/chrome/answer/142065',
                      '_blank'
                    );
                  }}
                  className="px-3 py-1.5 text-xs font-medium text-monta-black bg-monta-black-50 rounded-md hover:bg-monta-gray-100 transition-colors"
                >
                  {t('search.enableLocation')}
                </button>
              )}
              {isDisabled && (
                <button
                  onClick={() => getCurrentLocation()}
                  className="px-3 py-1.5 text-xs font-medium text-monta-black bg-monta-black-50 rounded-md hover:bg-monta-gray-100 transition-colors"
                >
                  {t('search.tryAgain')}
                </button>
              )}
            </div>
          </div>

          {/* Close button */}
          <button
            onClick={() =>
              useLocationStore.setState({ locationPermission: 'prompt' })
            }
            className="p-1 rounded-full hover:bg-monta-gray-100 transition-colors flex-shrink-0"
            aria-label={t('common.close')}
          >
            <svg
              className="w-4 h-4 text-monta-gray-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}
